'use client'

import { GradientTooltip } from '@/components/common/gradient-tooltip'
import { PricingSelect } from '@/components/pricing-v3/pricing-select'
import { SimplifiedSelector } from '@/components/pricing-v3/simplified-selector'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { CircleCheckIcon, InfoIcon, Shield, RefreshCw } from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslations } from 'next-intl'

interface SelectOption {
  value: string
  label: string
  price: number
}

interface IPaidPlanCardProps {
  plan: 'basic' | 'premium' | 'platinum'
  title: string
  tagline?: string
  description: string
  icon: string
  badge?: {
    text: string
    className: string
  }
  titleClassName?: string
  cardClassName?: string
  price: number
  isAnnual: boolean
  perMonthText: string
  billedAnnuallyText: (amount: string) => string
  credits: string
  setCredits: (value: string) => void
  creditOptions: SelectOption[]
  domains: string
  setDomains: (value: string) => void
  domainOptions: SelectOption[]
  showDomainSelect?: boolean
  getButtonText: (plan: 'basic' | 'premium' | 'platinum') => string
  getSubscriptionAction: (
    plan: 'basic' | 'premium' | 'platinum',
  ) => 'new' | 'current' | 'upgrade' | 'downgrade' | 'change'
  isButtonDisabled: (plan: 'basic' | 'premium' | 'platinum') => boolean
  handleSubscribe: (plan: 'basic' | 'premium' | 'platinum') => Promise<void>
  disabledTooltip: ReactNode | null
  keyOutcomes?: ReactNode
  features: ReactNode
  isCurrentPlan?: boolean
  shouldHighlight?: boolean
}

/**
 * Reusable paid plan card component for Basic, Premium, and Platinum plans
 */
export function PaidPlanCard({
  plan,
  title,
  tagline,
  description,
  icon,
  badge,
  titleClassName,
  cardClassName = 'bg-white/10 border-none text-white rounded-[20px]',
  price,
  isAnnual,
  perMonthText,
  billedAnnuallyText,
  credits,
  setCredits,
  creditOptions,
  domains,
  setDomains,
  domainOptions,
  showDomainSelect = true,
  getButtonText,
  getSubscriptionAction,
  isButtonDisabled,
  handleSubscribe,
  disabledTooltip,
  keyOutcomes,
  features,
  isCurrentPlan = false,
  shouldHighlight = false,
}: IPaidPlanCardProps) {
  const t = useTranslations('pricing-v3')
  const isDisabledWithTooltip = disabledTooltip !== null

  const button = (
    <Button
      className={cn(
        'w-full text-wrap font-semibold text-sm h-10 px-4 py-2 rounded-lg',
        isDisabledWithTooltip
          ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-70'
          : getSubscriptionAction(plan) === 'current'
            ? 'bg-white text-black cursor-not-allowed'
            : getSubscriptionAction(plan) === 'upgrade'
              ? plan === 'premium'
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-500/50'
                : 'bg-white text-gray-800 hover:bg-gray-50'
              : getSubscriptionAction(plan) === 'downgrade'
                ? 'bg-transparent border border-gray-600 text-gray-300 hover:bg-white/10 hover:border-gray-500'
                : plan === 'premium'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-500/50'
                  : 'bg-white text-black hover:bg-slate-100',
      )}
      onClick={() => {
        if (!isDisabledWithTooltip) {
          void handleSubscribe(plan)
        }
      }}
      disabled={isButtonDisabled(plan)}
    >
      {getButtonText(plan)}
    </Button>
  )

  const buttonWithTooltip = isDisabledWithTooltip ? (
    <div className="flex flex-col items-center space-y-2">
      {button}
      <span className="text-sm text-muted-foreground">{disabledTooltip}</span>
    </div>
  ) : (
    button
  )

  // Add highlighting for recommended plan (not current plan)
  const highlightedCardClassName =
    shouldHighlight && !isCurrentPlan
      ? cn(
          cardClassName,
          'ring-2 ring-[#FDD577] ring-offset-2 ring-offset-[#212121] shadow-lg shadow-[#FDD577]/30',
        )
      : cardClassName

  return (
    <Card className={highlightedCardClassName} data-plan={plan}>
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2 flex-wrap gap-2">
          <img src={icon} alt={title} className="w-7 h-7" />
          <span className={titleClassName}>{title}</span>
          {badge && <Badge className={badge.className}>{badge.text}</Badge>}
          {isCurrentPlan && (
            <Badge className="bg-[#FDD577] text-black hover:bg-[#FDD577]/90 text-xs font-semibold">
              {t('current_plan')}
            </Badge>
          )}
        </CardTitle>
        {/* Tagline - Scene化定位 */}
        {tagline && (
          <div className="text-sm text-white/80 font-normal mt-1">
            {tagline}
          </div>
        )}
        <CardDescription className="text-white opacity-60 font-light">
          {description}
        </CardDescription>
        <div className="pt-4 space-y-2">
          <div>
            <span className="text-4xl text-white font-semibold">
              ${price.toFixed(2)}
            </span>
            <span className="ml-2 text-white opacity-60">{perMonthText}</span>
          </div>
          {/* Show base credits clearly - no plus sign */}
          {plan === 'basic' && (
            <div className="text-base text-white/80">200 AI Credits/month</div>
          )}
          {plan === 'premium' && (
            <div className="text-base text-white/80">500 AI Credits/month</div>
          )}
          {plan === 'platinum' && (
            <div className="text-base text-white/80">1600 AI Credits/month</div>
          )}
          {isAnnual && (
            <div className="mt-2 space-y-1">
              <div className="text-sm text-white/70">
                {billedAnnuallyText((price * 12).toFixed(2))}
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 rounded-md">
                <span className="text-xs font-medium text-green-400">
                  Save ${((price / 0.8 - price) * 12).toFixed(0)}/year
                </span>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {/* Mobile: Simplified selector, Desktop: Original dropdown */}
          <div className="md:hidden">
            <SimplifiedSelector
              type="credits"
              options={creditOptions}
              value={credits}
              onChange={setCredits}
              isAnnual={isAnnual}
              plan={plan}
            />
          </div>
          <div className="hidden md:block">
            <PricingSelect
              options={creditOptions}
              value={credits}
              onChange={setCredits}
              isAnnual={isAnnual}
            />
          </div>

          {showDomainSelect && (
            <>
              <div className="md:hidden">
                <SimplifiedSelector
                  type="domains"
                  options={domainOptions}
                  value={domains}
                  onChange={setDomains}
                  isAnnual={isAnnual}
                  plan={plan}
                />
              </div>
              <div className="hidden md:block">
                <PricingSelect
                  options={domainOptions}
                  value={domains}
                  onChange={setDomains}
                  isAnnual={isAnnual}
                  isDomainSelect={true}
                />
              </div>
            </>
          )}
        </div>
        {buttonWithTooltip}

        {/* Mobile: Trust signals below CTA */}
        {!isDisabledWithTooltip && plan === 'premium' && (
          <div className="md:hidden flex items-center justify-center gap-4 text-xs text-white/70 pt-2 border-t border-white/10">
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              <span>{t('trust.secure_payment')}</span>
            </div>
            <div className="flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              <span>{t('trust.money_back')}</span>
            </div>
          </div>
        )}

        {/* Key Outcomes - 结果导向，不是功能列表 */}
        {keyOutcomes && (
          <div className="pt-4 border-t border-white/10">
            <div className="space-y-3 text-sm">{keyOutcomes}</div>
          </div>
        )}

        {/* Features - 功能清单 */}
        <div className="pt-4 border-t border-white/10">
          <div className="space-y-3 text-sm">{features}</div>
        </div>
      </CardContent>
    </Card>
  )
}

interface IFeatureRowProps {
  children: ReactNode
}

/**
 * Feature row with check icon
 */
export function FeatureRow({ children }: IFeatureRowProps) {
  return (
    <div className="w-full flex items-center space-x-2">
      <div className="flex-shrink-0">
        <CircleCheckIcon className="size-4 text-[#FDD577]" />
      </div>
      <div className="flex-1">{children}</div>
    </div>
  )
}

interface IFeatureWithTooltipProps {
  text: string
  tooltip: string
  className?: string
}

/**
 * Feature with tooltip icon
 */
export function FeatureWithTooltip({
  text,
  tooltip,
  className,
}: IFeatureWithTooltipProps) {
  return (
    <div className="flex items-center space-x-2">
      <span className={className}>{text}</span>
      <GradientTooltip content={tooltip} side="top">
        <InfoIcon className="w-4 h-4 cursor-help flex-shrink-0" />
      </GradientTooltip>
    </div>
  )
}
